import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { PageSEO } from "../hooks/useSEO";
import "../styles/styles.css";

const Privacy = () => {
  const { lang } = useLang();
  const isEN = lang === "en";

  const sections = isEN ? [
    {
      title: "1. Who We Are",
      body: "AIVEO (www.aiveo.net) is an AI cinema platform in beta. For privacy matters, contact us at aiveoaicinema@gmail.com.",
    },
    {
      title: "2. Data We Collect",
      body: "We collect: (a) Account data — email address and password (stored locally in your browser during beta). (b) Usage data — pages visited, content viewed and interactions with the platform (anonymised). (c) Technical data — browser type, device type, IP address and approximate location for security and performance purposes. We do not currently collect payment information.",
    },
    {
      title: "3. How We Use Your Data",
      body: "We use your data to: provide and improve the platform; personalise your experience (e.g. continue watching, favourites); communicate important service updates; prevent fraud and abuse; comply with legal obligations.",
    },
    {
      title: "4. Cookies and Local Storage",
      body: "AIVEO uses browser localStorage to store your session, preferences and content history. We use reCAPTCHA Enterprise (Google) for security on forms, which may set cookies. We do not use advertising cookies or tracking cookies from ad networks.",
    },
    {
      title: "5. Third-Party Services",
      body: "We use: Google reCAPTCHA Enterprise — for form security (subject to Google's Privacy Policy). Vercel — for platform hosting (subject to Vercel's Privacy Policy). Google Cloud — for backend services (subject to Google Cloud's Privacy Policy). We do not sell your personal data to third parties.",
    },
    {
      title: "6. Data Retention",
      body: "During beta, most user data is stored locally in your browser (localStorage). If you clear your browser data, your account data will be lost. We are building server-side storage for future versions.",
    },
    {
      title: "7. Your Rights",
      body: "Under applicable data protection laws (including GDPR where applicable), you have the right to: access your personal data; correct inaccurate data; request deletion of your data; object to processing; data portability. To exercise these rights, contact aiveoaicinema@gmail.com.",
    },
    {
      title: "8. Children",
      body: "AIVEO is not directed at children under 13. We do not knowingly collect personal data from children under 13. If you believe we have inadvertently collected such data, please contact us immediately.",
    },
    {
      title: "9. Changes to This Policy",
      body: "We may update this Privacy Policy from time to time. We will notify users of significant changes via a notice on the platform. Your continued use after changes constitutes acceptance.",
    },
    {
      title: "10. Contact",
      body: "For privacy enquiries: aiveoaicinema@gmail.com",
    },
  ] : [
    {
      title: "1. Quiénes Somos",
      body: "AIVEO (www.aiveo.net) es una plataforma de cine con IA en beta. Para asuntos de privacidad, contáctanos en aiveoaicinema@gmail.com.",
    },
    {
      title: "2. Datos que Recopilamos",
      body: "Recopilamos: (a) Datos de cuenta — dirección de correo electrónico y contraseña (almacenados localmente en tu navegador durante la beta). (b) Datos de uso — páginas visitadas, contenido visto e interacciones con la plataforma (anonimizados). (c) Datos técnicos — tipo de navegador, tipo de dispositivo, dirección IP y ubicación aproximada con fines de seguridad y rendimiento. Actualmente no recopilamos información de pago.",
    },
    {
      title: "3. Cómo Usamos tus Datos",
      body: "Usamos tus datos para: proporcionar y mejorar la plataforma; personalizar tu experiencia (p.ej. continuar viendo, favoritos); comunicar actualizaciones importantes del servicio; prevenir fraudes y abusos; cumplir con obligaciones legales.",
    },
    {
      title: "4. Cookies y Almacenamiento Local",
      body: "AIVEO usa localStorage del navegador para almacenar tu sesión, preferencias e historial de contenido. Usamos reCAPTCHA Enterprise (Google) para seguridad en formularios, que puede establecer cookies. No usamos cookies publicitarias ni cookies de seguimiento de redes publicitarias.",
    },
    {
      title: "5. Servicios de Terceros",
      body: "Usamos: Google reCAPTCHA Enterprise — para seguridad en formularios (sujeto a la Política de Privacidad de Google). Vercel — para el alojamiento de la plataforma (sujeto a la Política de Privacidad de Vercel). Google Cloud — para servicios de backend (sujeto a la Política de Privacidad de Google Cloud). No vendemos tus datos personales a terceros.",
    },
    {
      title: "6. Retención de Datos",
      body: "Durante la beta, la mayoría de los datos de usuario se almacenan localmente en tu navegador (localStorage). Si borras los datos de tu navegador, se perderán los datos de tu cuenta. Estamos construyendo almacenamiento del lado del servidor para versiones futuras.",
    },
    {
      title: "7. Tus Derechos",
      body: "Bajo las leyes de protección de datos aplicables (incluido el RGPD donde sea aplicable), tienes derecho a: acceder a tus datos personales; corregir datos inexactos; solicitar la eliminación de tus datos; oponerte al tratamiento; portabilidad de datos. Para ejercer estos derechos, contacta con aiveoaicinema@gmail.com.",
    },
    {
      title: "8. Menores",
      body: "AIVEO no está dirigido a menores de 13 años. No recopilamos intencionadamente datos personales de menores de 13 años. Si crees que hemos recopilado inadvertidamente dichos datos, contáctanos de inmediato.",
    },
    {
      title: "9. Cambios en Esta Política",
      body: "Podemos actualizar esta Política de Privacidad de vez en cuando. Notificaremos a los usuarios de cambios significativos mediante un aviso en la plataforma. El uso continuado tras los cambios constituye la aceptación.",
    },
    {
      title: "10. Contacto",
      body: "Para consultas de privacidad: aiveoaicinema@gmail.com",
    },
  ];

  return (
    <div className="legal-page">
      <PageSEO
        title={isEN ? "Privacy Policy" : "Política de Privacidad"}
        description={isEN ? "Read AIVEO's privacy policy. We respect your data and are committed to transparency." : "Lee la política de privacidad de AIVEO. Respetamos tus datos y nos comprometemos con la transparencia."}
        url="/privacy"
      />
      <div className="legal-container">
        <div className="legal-hero">
          <h1>{isEN ? "Privacy Policy" : "Política de Privacidad"}</h1>
          <p className="legal-meta">{isEN ? "Last updated: June 2025 · Applies to: www.aiveo.net" : "Última actualización: Junio 2025 · Aplicable a: www.aiveo.net"}</p>
        </div>
        {sections.map((s) => (
          <section key={s.title} className="legal-section">
            <h2>{s.title}</h2>
            <p>{s.body}</p>
          </section>
        ))}
        <div className="legal-footer-nav">
          <Link to="/about"  className="legal-nav-link">{isEN ? "About" : "Sobre AIVEO"}</Link>
          <Link to="/terms"  className="legal-nav-link">{isEN ? "Terms of Use" : "Términos de Uso"}</Link>
          <Link to="/ai-policy" className="legal-nav-link">{isEN ? "AI Content Policy" : "Política de Contenido IA"}</Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
